/**
 * Adapted from PlayCanvas Engine's scripts/esm/gsplat/gsplat-flipbook.mjs at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import type { GSplatResourceLease } from '@/plugins/gsplat/GSplatHandleBinding'
import { getGSplatUrlBasename, getGSplatUrlExtension } from '@/plugins/gsplat/loaders/GSplatHandler'
import type { GaussianSplatLoadRequest } from '@/plugins/gsplat/types/PluginTypes'
import type { Disposable } from '@/shared/types/core'

type CacheEntryStatus = 'loading' | 'ready' | 'failed' | 'retired'

interface CacheEntry {
  readonly key: string
  readonly request: Readonly<GaussianSplatLoadRequest>
  readonly controller: AbortController
  readonly settled: Promise<void>
  status: CacheEntryStatus
  refCount: number
  resource: GSplatResourceLease | null
  error: unknown
  retireRequested: boolean
}

export interface GSplatFlipbookResourceLoader {
  loadResource(request: Readonly<GaussianSplatLoadRequest>): Promise<GSplatResourceLease>
}

export interface GSplatFlipbookAssetLease extends Disposable {
  readonly key: string
  readonly url: string
  readonly ready: boolean
  readonly failed: boolean
  waitUntilReady(): Promise<void>
  acquireResource(): GSplatResourceLease
  release(): void
}

export class GSplatFlipbookCacheKeyError extends Error {
  readonly code = 'GSPLAT_FLIPBOOK_CACHE_KEY_INVALID'

  constructor(
    readonly path: string,
    message: string
  ) {
    super(`GSplatFlipbook cache key ${path}: ${message}`)
    this.name = 'GSplatFlipbookCacheKeyError'
  }
}

/**
 * System-owned 的 Flipbook Resource cache。
 *
 * 每个实例只属于一个 GSplatSystem，所以 cache identity 天然包含 owning System；entry 只持有共享
 * Resource master lease，永远不创建或持有公共 Handle。
 */
export class GSplatFlipbookAssetCache implements Disposable {
  private readonly entries = new Map<string, CacheEntry>()
  private disposed = false

  constructor(private readonly loader: GSplatFlipbookResourceLoader) {}

  get entryCount(): number {
    return this.entries.size
  }

  acquire(request: Readonly<GaussianSplatLoadRequest>): GSplatFlipbookAssetLease {
    this.assertActive()
    const normalizedRequest = normalizeRequest(request)
    const key = createCacheKey(normalizedRequest)
    let entry = this.entries.get(key)

    if (entry?.status === 'retired' || (entry?.status === 'failed' && entry.refCount === 0)) {
      this.entries.delete(key)
      entry.resource?.dispose()
      entry = undefined
    }

    if (!entry) {
      entry = this.createEntry(key, normalizedRequest)
      this.entries.set(key, entry)
    }

    entry.refCount++
    entry.retireRequested = false
    return new FlipbookAssetLease(this, entry)
  }

  /** 最后一个 release 只标记 retire；真正 abort/unload 固定在 System logical frame sweep。 */
  processRetired(): void {
    if (this.disposed) return
    for (const [key, entry] of this.entries) {
      if (!entry.retireRequested || entry.refCount > 0) continue
      this.entries.delete(key)
      entry.status = 'retired'
      entry.controller.abort()
      entry.resource?.dispose()
      entry.resource = null
    }
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    for (const entry of this.entries.values()) {
      entry.status = 'retired'
      entry.controller.abort()
      entry.resource?.dispose()
      entry.resource = null
    }
    this.entries.clear()
  }

  release(entry: CacheEntry): void {
    if (entry.refCount <= 0) return
    entry.refCount--
    if (entry.refCount === 0) entry.retireRequested = true
  }

  isLeaseReady(entry: CacheEntry): boolean {
    return !this.disposed && this.entries.get(entry.key) === entry && entry.status === 'ready'
  }

  isLeaseFailed(entry: CacheEntry): boolean {
    return entry.status === 'failed'
  }

  async waitUntilReady(entry: CacheEntry): Promise<void> {
    await entry.settled
    if (entry.status === 'ready' && !this.disposed && this.entries.get(entry.key) === entry) return
    if (entry.status === 'failed') throw entry.error
    throw abortError()
  }

  acquireResource(entry: CacheEntry): GSplatResourceLease {
    if (!this.isLeaseReady(entry) || !entry.resource) {
      if (entry.status === 'failed') throw entry.error
      throw new Error(`GSplatFlipbookAssetCache: Resource 尚未 ready，url=${entry.request.url}`)
    }
    return entry.resource.acquire()
  }

  private createEntry(key: string, request: Readonly<GaussianSplatLoadRequest>): CacheEntry {
    const controller = new AbortController()
    const loadRequest = Object.freeze({ ...request, signal: controller.signal })
    // 对齐上游 miss 的 construct/add/load/publish：先启动统一 loader，再把 entry 发布到 Map。
    let entry!: CacheEntry
    const loading = this.loader.loadResource(loadRequest)
    const settled = loading.then(
      (resource) => {
        if (this.disposed || entry.status === 'retired' || controller.signal.aborted) {
          resource.dispose()
          return
        }
        entry.resource = resource
        entry.status = 'ready'
      },
      (error: unknown) => {
        if (entry.status === 'retired' || controller.signal.aborted) return
        entry.error = error
        entry.status = 'failed'
      }
    )
    entry = {
      key,
      request,
      controller,
      settled,
      status: 'loading',
      refCount: 0,
      resource: null,
      error: undefined,
      retireRequested: false,
    }
    return entry
  }

  private assertActive(): void {
    if (this.disposed) throw new Error('GSplatFlipbookAssetCache 已销毁')
  }
}

class FlipbookAssetLease implements GSplatFlipbookAssetLease {
  private released = false

  constructor(
    private readonly cache: GSplatFlipbookAssetCache,
    private readonly entry: CacheEntry
  ) {}

  get key(): string {
    return this.entry.key
  }

  get url(): string {
    return this.entry.request.url
  }

  get ready(): boolean {
    return !this.released && this.cache.isLeaseReady(this.entry)
  }

  get failed(): boolean {
    return !this.released && this.cache.isLeaseFailed(this.entry)
  }

  async waitUntilReady(): Promise<void> {
    this.assertActive()
    await this.cache.waitUntilReady(this.entry)
    this.assertActive()
  }

  acquireResource(): GSplatResourceLease {
    this.assertActive()
    return this.cache.acquireResource(this.entry)
  }

  dispose(): void {
    this.release()
  }

  release(): void {
    if (this.released) return
    this.released = true
    this.cache.release(this.entry)
  }

  private assertActive(): void {
    if (this.released) throw abortError()
  }
}

function normalizeRequest(
  request: Readonly<GaussianSplatLoadRequest>
): Readonly<GaussianSplatLoadRequest> {
  const url = normalizeUrl(request.url)
  const format = request.format?.trim().toLowerCase() || undefined
  const isPly = (format ?? inferFormat(url)) === 'ply'
  const loaderOptions = Object.freeze({
    ...(request.loaderOptions ?? {}),
    ...(isPly ? { reorder: false } : {}),
  })
  // 在调用 loader 前完成 keyability 校验，避免已启动请求后才失败。
  stableSerialize(loaderOptions, 'loaderOptions')
  stableSerialize(request.options ?? {}, 'options')
  return Object.freeze({
    ...request,
    url,
    format,
    loaderOptions,
    signal: undefined,
  })
}

function createCacheKey(request: Readonly<GaussianSplatLoadRequest>): string {
  return [
    request.url,
    request.format ?? inferFormat(request.url) ?? '',
    stableSerialize(request.loaderOptions ?? {}, 'loaderOptions'),
    stableSerialize(request.options ?? {}, 'options'),
  ].join('\u0000')
}

function stableSerialize(value: unknown, path: string): string {
  if (value === null) return 'null'
  switch (typeof value) {
    case 'string':
      return JSON.stringify(value)
    case 'boolean':
      return value ? 'true' : 'false'
    case 'number':
      if (!Number.isFinite(value)) throw new GSplatFlipbookCacheKeyError(path, 'number 必须有限')
      return Object.is(value, -0) ? '-0' : String(value)
    case 'undefined':
      return 'undefined'
    case 'object': {
      if (Array.isArray(value)) {
        return `[${value.map((item, index) => stableSerialize(item, `${path}[${index}]`)).join(',')}]`
      }
      const prototype = Object.getPrototypeOf(value)
      if (prototype !== Object.prototype && prototype !== null) {
        throw new GSplatFlipbookCacheKeyError(path, '只支持 plain object/array/primitive')
      }
      const record = value as Record<string, unknown>
      return `{${Object.keys(record)
        .sort()
        .map((key) => `${JSON.stringify(key)}:${stableSerialize(record[key], `${path}.${key}`)}`)
        .join(',')}}`
    }
    default:
      throw new GSplatFlipbookCacheKeyError(path, `不支持 ${typeof value}`)
  }
}

function normalizeUrl(url: string): string {
  if (typeof url !== 'string' || !url.trim()) {
    throw new GSplatFlipbookCacheKeyError('url', '必须是非空字符串')
  }
  const source = url.trim()
  const absolutePattern = /^[a-z][a-z\d+.-]*:/i
  try {
    if (absolutePattern.test(source)) return new URL(source).href
    if (source.startsWith('/')) {
      const base = globalThis.location?.origin ?? 'https://share3d.invalid'
      const normalized = new URL(source, base)
      return globalThis.location?.origin
        ? normalized.href
        : `${normalized.pathname}${normalized.search}${normalized.hash}`
    }
    const base = globalThis.location?.href ?? 'https://share3d.invalid/'
    const normalized = new URL(source, base)
    if (globalThis.location?.href) return normalized.href
    return `${normalized.pathname.slice(1)}${normalized.search}${normalized.hash}`
  } catch {
    throw new GSplatFlipbookCacheKeyError('url', `无法规范化 "${url}"`)
  }
}

function inferFormat(url: string): string | undefined {
  const basename = getGSplatUrlBasename(url)
  if (basename === 'lod-meta.json') return 'lod-meta'
  const extension = getGSplatUrlExtension(url)
  return extension || undefined
}

function abortError(): DOMException {
  return new DOMException('Aborted', 'AbortError')
}
